import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Globe, Menu, X, Plus, User, Crown, Shield, LogOut, Scan, Trophy } from 'lucide-react';
import { useAuth, LoginForm, UserProfile } from './UserAuth';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

export const Header = ({ headerOverride }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [userPoints, setUserPoints] = useState(0);

  // Allow store/club pages to override header colors
  const h = headerOverride || theme.header;

  // Load user points when user changes
  useEffect(() => {
    if (user?.email) {
      loadUserPoints();
    }
  }, [user]);

  const loadUserPoints = async () => {
    try {
      const data = await api.getUserPoints(user.email);
      setUserPoints(data.totalPoints || 0);
    } catch (error) {
      console.error('Error loading points:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 backdrop-blur-md border-b ${h.bg} ${h.border}`} style={{ zIndex: 1000, fontFamily: h.fontFamily || 'inherit' }}>
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - Clickable with Home Icon */}
            <button
              className="flex items-center space-x-1 sm:space-x-2 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none bg-transparent border-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Home button clicked, navigating to /');
                navigate('/');
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              title="Go to Home"
              type="button"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-50 rounded-xl flex items-center justify-center shadow-md">
                <Globe className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-800" />
              </div>
              <div className="flex flex-col">
                <span className={`text-lg sm:text-2xl font-bold ${h.text} leading-tight`}>iWasHere.me</span>
                <span className="text-[8px] sm:text-[10px] text-emerald-100 -mt-1">formerly TrackMe.info</span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className={`${h.buttonSecondary} transition-colors font-medium`}
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('map')} 
                className={`${h.buttonSecondary} transition-colors font-medium`}
              >
                Live Map
              </button>
              <button 
                onClick={() => scrollToSection('stories')} 
                className={`${h.buttonSecondary} transition-colors font-medium`}
              >
                Stories
              </button>
              <button 
                onClick={() => navigate('/store')} 
                className={`${h.buttonSecondary} transition-colors font-medium`}
              >
                🛍️ Store
              </button>
              <button 
                onClick={() => navigate('/subscription')} 
                className={`${h.buttonSecondary} transition-colors font-medium`}
              >
                💎 Subscription
              </button>
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Create Guestbook Button - Always visible */}
              <Button
                onClick={() => navigate('/create-location')}
                className="bg-white hover:bg-gray-100 text-emerald-700 font-semibold shadow-md hover:shadow-lg transition-all"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Guestbook
              </Button>
              
              {/* User Authentication */}
              {user ? (
                <div className="flex items-center gap-2">
                  {user.role === 'admin' ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowUserProfile(true)}
                      className="border-2 border-white/70 bg-white/20 text-white hover:bg-white/30 cursor-pointer"
                      type="button"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      {user.name}
                      <Badge className="ml-2 bg-yellow-500 text-white text-xs">Admin</Badge>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setShowUserProfile(true)}
                      className="border-2 border-white/70 bg-white/20 text-white hover:bg-white/30"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {user.name}
                    </Button>
                  )}
                  
                  {/* Points Display & Leaderboard */}
                  <div 
                    onClick={() => navigate('/leaderboard')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/90 rounded-full border border-white cursor-pointer hover:shadow-md transition-all"
                    title="View Leaderboard"
                  >
                    <Trophy className="w-4 h-4 text-yellow-600" />
                    <span className="font-bold text-gray-800">{userPoints.toLocaleString()}</span>
                    <span className="text-xs text-gray-600">pts</span>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    className="border-2 border-white/70 bg-red-500/80 text-white hover:bg-red-600"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowLoginForm(true)}
                  className="border-2 border-white/70 bg-white/20 text-white hover:bg-white/30"
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-3 -mr-3 text-white hover:text-emerald-100 transition-colors touch-manipulation"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Menu - Outside header for proper z-index stacking */}
      {isMenuOpen && (
        <>
          {/* Backdrop overlay - starts below header to not block X button */}
          <div 
            className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-black/30" 
            style={{ zIndex: 1001 }}
            onClick={() => setIsMenuOpen(false)}
          />
          {/* Menu panel */}
          <div 
            className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-white shadow-lg border-t border-gray-200 overflow-y-auto"
            style={{ zIndex: 1002 }}
          >
            <nav className="flex flex-col space-y-2 p-4 pb-20">
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="text-left text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('map')} 
                className="text-left text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                Live Map
              </button>
              <button 
                onClick={() => scrollToSection('stories')} 
                className="text-left text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                Stories
              </button>
              <button 
                onClick={() => {
                  navigate('/store');
                  setIsMenuOpen(false);
                }} 
                className="text-left text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                🛍️ Store
              </button>
              <button 
                onClick={() => {
                  navigate('/subscription');
                  setIsMenuOpen(false);
                }} 
                className="text-left text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                💎 Subscription
              </button>
              
              <div className="flex flex-col space-y-3 pt-4 mt-2 border-t border-gray-200">
                <Button 
                  className="h-12 text-base bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold"
                  onClick={() => {
                    navigate('/create-location');
                    setIsMenuOpen(false);
                  }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Guestbook
                </Button>
                
                <Button 
                  variant="outline"
                  className="h-12 text-base border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => {
                    navigate('/create-item');
                    setIsMenuOpen(false);
                  }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Track an Item
                </Button>

                {user ? (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowUserProfile(true);
                        setIsMenuOpen(false);
                      }}
                      className="w-full justify-start h-12 text-base border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                    >
                      {user.role === 'admin' ? <Crown className="w-5 h-5 mr-2" /> : <User className="w-5 h-5 mr-2" />}
                      {user.name}
                      {user.role === 'admin' && <Badge className="ml-2 bg-yellow-600 text-white text-xs">Admin</Badge>}
                    </Button>
                    
                    {/* Admin Dashboard Access for Mobile */}
                    {user.role === 'admin' && (
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate('/admin/dashboard');
                          setIsMenuOpen(false);
                        }}
                        className="w-full justify-start h-12 text-base border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
                        type="button"
                      >
                        <Shield className="w-5 h-5 mr-2" />
                        Admin Dashboard
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        logout();
                        navigate('/');
                        setIsMenuOpen(false);
                      }}
                      className="w-full justify-start h-12 text-base border-2 border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-5 h-5 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowLoginForm(true);
                      setIsMenuOpen(false);
                    }}
                    className="w-full justify-start h-12 text-base border-2 border-gray-300 hover:border-emerald-600 hover:text-emerald-600"
                  >
                    <User className="w-5 h-5 mr-2" />
                    Login / Sign Up
                  </Button>
                )}
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Auth Modals */}
      {showLoginForm && (
        <LoginForm onClose={() => setShowLoginForm(false)} />
      )}
      
      {showUserProfile && user && (
        <UserProfile user={user} onClose={() => setShowUserProfile(false)} />
      )}
    </>
  );
};